/**
 * Azure AD Login Component
 * 
 * VERSION: 2.0.1 - Enhanced Logging (March 9, 2026)
 * 
 * This component provides a "Login with Microsoft" button that:
 * 1. Opens Azure AD login popup (user enters Microsoft credentials)
 * 2. Azure AD returns a REAL access token (not a test token)
 * 3. Sends the real token to backend for validation
 * 4. Stores token for authenticated API requests
 * 
 * The token is obtained directly from Microsoft Azure AD - it's a real,
 * cryptographically signed JWT token that proves the user's identity.
 * 
 * Usage:
 * import { LoginButton } from './auth/LoginButton';
 * <LoginButton onLoginSuccess={(user) => console.log('Logged in:', user)} />
 */

import { useState } from 'react';
import { msalConfig, loginRequest, tokenRequest } from './msalConfig';
import axios from '../api/axios';
import { msalInstance } from './msalInstance';

// The MSAL instance lives in ./msalInstance (the shared axios instance asks it for fresh tokens);
// re-exported here so every existing `import { msalInstance } from './auth/LoginButton'` keeps working.
export { msalInstance };

interface LoginButtonProps {
  onLoginSuccess?: (user: any) => void;
  onLoginError?: (error: Error) => void;
}

export const LoginButton = ({ onLoginSuccess, onLoginError }: LoginButtonProps) => {
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleLogin = async () => {
    console.log('[LOGIN CLICK] Button clicked! isLoggingIn:', isLoggingIn);
    
    if (isLoggingIn) {
      console.log('[LOGIN CLICK] Already logging in, ignoring click');
      return;
    }
    
    setIsLoggingIn(true);
    console.log('[LOGIN CLICK] Set isLoggingIn to true');
    
    try {
      // Initialize MSAL (idempotent — safe to call multiple times)
      await msalInstance.initialize();
      // Clear any stale interaction lock from a previous failed redirect
      await msalInstance.handleRedirectPromise();

      console.log('[LOGIN] Login request config:', loginRequest);
      console.log('[LOGIN] MSAL config:', msalConfig);
      
      // Redirect to Microsoft login page
      // Note: After this line, the page will redirect away
      // The rest of the login flow happens in handleRedirectCallback()
      // Mark that we're expecting an OAuth redirect back
      sessionStorage.setItem('msal_redirect_pending', '1');
      console.log('[LOGIN] Calling loginRedirect...');
      await msalInstance.loginRedirect(loginRequest);
      console.log('[LOGIN] loginRedirect called (this line may not show if redirect is immediate)');
      // Code after this line will not execute because page redirects away
      
    } catch (error: any) {
      console.error('[LOGIN ERROR] ❌ Login failed:', error);
      console.error('[LOGIN ERROR] Error details:', {
        message: error.message,
        errorCode: error.errorCode,
        errorMessage: error.errorMessage,
        stack: error.stack
      });
      if (error.errorCode === 'popup_window_error') {
        alert('Popup was blocked. Please allow popups for this site and try again.');
      } else {
        alert('Login failed: ' + (error.message || 'Unknown error'));
      }
      onLoginError?.(error);
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <button
      onClick={handleLogin}
      disabled={isLoggingIn}
      style={{
        padding: '14px 32px',
        background: isLoggingIn ? '#666' : 'var(--button-bg)',
        color: isLoggingIn ? '#ccc' : 'var(--button-text)',
        border: 'none',
        borderRadius: '10px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: isLoggingIn ? 'not-allowed' : 'pointer',
        boxShadow: isLoggingIn ? 'none' : '0 4px 20px rgba(0,0,0,0.2)',
        transition: 'all 0.3s',
        width: '100%'
      }}
      onMouseOver={(e) => {
        if (!isLoggingIn) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 30px rgba(0,0,0,0.3)';
          e.currentTarget.style.background = 'var(--button-hover)';
        }
      }}
      onMouseOut={(e) => {
        if (!isLoggingIn) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)';
          e.currentTarget.style.background = 'var(--button-bg)';
        }
      }}
    >
      {isLoggingIn ? 'Logging in...' : 'Login with Microsoft'}
    </button>
  );
};

/**
 * Attempt silent SSO authentication using a login hint (e.g. from ModoLabs URL param).
 * Uses Azure AD's ssoSilent — no redirect, no popup, no user interaction.
 * Returns the validated user object on success, or null if no active Azure AD session exists.
 */
export const attemptSilentAuth = async (loginHint: string): Promise<any | null> => {
  try {
    await msalInstance.initialize();
    console.log('[SILENT AUTH] Attempting ssoSilent for:', loginHint);

    const silentResponse = await msalInstance.ssoSilent({
      ...loginRequest,
      loginHint,
    });

    if (!silentResponse?.account) {
      console.log('[SILENT AUTH] ssoSilent returned no account');
      return null;
    }

    // Acquire backend-scoped token silently
    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      account: silentResponse.account,
    });

    const accessToken = tokenResponse?.accessToken;
    if (!accessToken) {
      console.log('[SILENT AUTH] No access token after ssoSilent');
      return null;
    }

    // Validate with backend
    const backendResponse = await axios.post('/auth/login', { access_token: accessToken });
    const { user, access_token: storedToken } = backendResponse.data;

    sessionStorage.setItem('access_token', storedToken);
    sessionStorage.setItem('user', JSON.stringify(user));
    axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    axios.defaults.headers.common['x-dev-user-id'] = user.email;
    axios.defaults.headers.common['x-dev-user-name'] = user.name || user.email;

    console.log('[SILENT AUTH] ✅ Silent login successful for', user.email);
    return user;
  } catch (error: any) {
    // Expected failure — Azure AD has no active session in this webview
    console.log('[SILENT AUTH] Silent auth failed (no session):', error?.errorCode || error?.message);
    return null;
  }
};

/**
 * Handle OAuth Redirect Callback
 * This function processes the OAuth callback after redirect from Azure AD
 */
export const handleRedirectCallback = async () => {
  try {
    // Log current URL to debug redirect issues
    console.log('[DEBUG] Current URL:', window.location.href);
    console.log('[DEBUG] URL hash:', window.location.hash);
    console.log('[DEBUG] URL search:', window.location.search);
    
    // Initialize MSAL first
    await msalInstance.initialize();
    console.log('[DEBUG] handleRedirectCallback: MSAL initialized');
    console.log('[DEBUG] MSAL config:', msalConfig);
    console.log('[DEBUG] All accounts:', msalInstance.getAllAccounts());
    
    // Handle the redirect promise (only returns non-null if we just came back from OAuth redirect)
    const response = await msalInstance.handleRedirectPromise();
    console.log('[DEBUG] handleRedirectCallback: MSAL response', response);
    if (response) {
      console.log('✓ Redirect callback received from Azure AD');
      console.log('✓ User authenticated:', response.account?.username);
      
      // Acquire access token with backend API scope (includes app roles)
      console.log('Acquiring access token for backend API...');
      let tokenResponse;
      try {
        tokenResponse = await msalInstance.acquireTokenSilent({
          ...tokenRequest,
          account: response.account || msalInstance.getAllAccounts()[0]
        });
      } catch (silentError) {
        // If silent fails (e.g., due to X-Frame-Options), use redirect instead of popup
        await msalInstance.acquireTokenRedirect({
          ...tokenRequest,
          account: response.account || msalInstance.getAllAccounts()[0]
        });
        // This line won't be reached as redirect navigates away
        return;
      }
      
      const accessToken = tokenResponse?.accessToken;
      // Never log the token itself: anyone who copies it from the console (or a screenshot) can use it.
      console.log('✓ Access token acquired with backend API scope');
      console.log('✓ Access token includes app roles (Survey.Admin, Campaign.Manager, etc.)');
      
      // Decode token to log roles (for debugging)
      try {
        const tokenParts = accessToken.split('.');
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('✓ Token roles:', payload.roles || 'No roles found');
        console.log('✓ Token audience:', payload.aud);
      } catch (e) {
        console.warn('Could not decode token for debugging');
      }
      
      // Validate token with backend
      console.log('Sending access token to backend for validation...');
      const backendResponse = await axios.post('/auth/login', {
        access_token: accessToken
      });
      
      const { user, access_token } = backendResponse.data;
      console.log('✓ Backend validated token. User:', user);
      
      // Store token and user info
      sessionStorage.setItem('access_token', access_token);
      sessionStorage.setItem('user', JSON.stringify(user));
      
      // Set axios authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Set user ID and name for requests
      axios.defaults.headers.common['x-dev-user-id'] = user.email;
      axios.defaults.headers.common['x-dev-user-name'] = user.name || user.email;
      
      console.log('✅ Login complete!');
      
      return user;
    }
    
    // No redirect response means we didn't just come back from OAuth
    console.log('[DEBUG] handleRedirectCallback: No redirect response from MSAL');
    return null;
  } catch (error) {
    console.error('[DEBUG] handleRedirectCallback: Error', error);
    throw error;
  }
};

/**
 * Logout Function
 * Properly signs out from both the application and Azure AD
 */
// localStorage keys that should survive logout (user preferences / cross-session data)
const PERSISTENT_LOCAL_KEYS = ['ecm_from_addresses', 'admin-theme', 'admin-theme-version'];

/** Clear localStorage preserving user preference keys that outlive a session. */
function clearLocalStorageExceptPersistent() {
  const preserved: Record<string, string> = {};
  PERSISTENT_LOCAL_KEYS.forEach(k => {
    const v = localStorage.getItem(k);
    if (v !== null) preserved[k] = v;
  });
  localStorage.clear();
  Object.entries(preserved).forEach(([k, v]) => localStorage.setItem(k, v));
}

export const logout = async () => {
  try {
    // Initialize MSAL before any operations
    await msalInstance.initialize();
    
    // Clear session storage and non-persistent local storage
    sessionStorage.clear();
    clearLocalStorageExceptPersistent();
    
    // Clear axios headers
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['x-dev-user-id'];
    
    // Get the current account
    const accounts = msalInstance.getAllAccounts();
    
    if (accounts.length > 0) {
      // Sign out from Azure AD - this will:
      // 1. Clear MSAL cache
      // 2. Clear Azure AD session
      // 3. Redirect to postLogoutRedirectUri (home page)
      await msalInstance.logoutRedirect({
        account: accounts[0],
        postLogoutRedirectUri: window.location.origin,
      });
      // Note: Code after this won't execute as page redirects
    } else {
      // No Azure AD session, just redirect home
      window.location.href = '/';
    }
  } catch (error) {
    console.error('Logout error:', error);
    // Fallback: clear everything and redirect home
    sessionStorage.clear();
    clearLocalStorageExceptPersistent();
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['x-dev-user-id'];
    window.location.href = '/';
  }
};

/**
 * Get Current User from Session
 */
export const getCurrentUser = () => {
  const userStr = sessionStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

/**
 * Check if User is Authenticated
 */
export const isAuthenticated = () => {
  return !!sessionStorage.getItem('access_token');
};
